import React, { useState, useRef } from "react"
import { Dialog } from "primereact/dialog"
import { FileUpload, FileUploadSelectEvent } from "primereact/fileupload"
import { InputTextarea } from "primereact/inputtextarea"
import {
  InputNumber,
  InputNumberValueChangeEvent,
} from "primereact/inputnumber"
import { Calendar } from "primereact/calendar"
import { Dropdown } from "primereact/dropdown"
import { Toast } from "primereact/toast"

import { createPhoto } from "../api/photo"
import { CreatePhotoInput } from "../graphql/schema"

import { apiPrefix, getAuthHeader } from "../api/api"
import { FileUploadRes } from "../sharedTypes/responseData"
import { StButton, StInputText } from "../ui/ui"

// 补全初始状态
const initialPhotoState: Omit<CreatePhotoInput, "fileId" | "authorId"> = {
  title: "",
  slug: "",
  altText: "",
  description: "",
  status: "published",
  gallerySlug: "",
  locationData: "",
  exifData: "",
  dateTaken: "",
  width: undefined, // undefined 对应 GraphQL 的 InputMaybe
  height: undefined,
}

// 状态选项
const statusOptions = [
  { label: "Draft", value: "draft" },
  { label: "Private", value: "private" },
  { label: "Published", value: "published" },
  { label: "Archived", value: "archived" },
]

interface PhotoUploadProps {
  onUploadSuccess: () => void
  authorId: string
}

export function PhotoUpload({ onUploadSuccess, authorId }: PhotoUploadProps) {
  const [dialogVisible, setDialogVisible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [photoData, setPhotoData] = useState(initialPhotoState)
  const toast = useRef<Toast>(null)

  // 通用 Input 变化处理 (Text, Textarea)
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setPhotoData((prev) => ({ ...prev, [name]: value }))
  }

  // 处理 PrimeReact 特殊组件的值变化 (Dropdown, Calendar, InputNumber)
  const handleValueChange = (
    name: keyof typeof initialPhotoState,
    value: any
  ) => {
    setPhotoData((prev) => ({ ...prev, [name]: value }))
  }

  // 处理文件选择
  const handleFileSelect = (event: FileUploadSelectEvent) => {
    if (event.files && event.files.length > 0) {
      const file = event.files[0]
      setSelectedFile(file)
    }
  }

  const resetAndCloseDialog = () => {
    setDialogVisible(false)
    setSelectedFile(null)
    setPhotoData(initialPhotoState)
    setIsSubmitting(false)
  }

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select a file to upload.",
      })
      return
    }
    if (!photoData.title || !photoData.slug) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "Title and Slug are required.",
      })
      return
    }

    setIsSubmitting(true)

    try {
      // --- Step 1: Upload ---
      const file = selectedFile
      const fileUploadResponse = await fetch(`${apiPrefix}/upload`, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
          "X-File-Name": encodeURIComponent(file.name),
          ...getAuthHeader(),
        },
        body: file,
      })

      if (!fileUploadResponse.ok) {
        throw new Error(`File upload failed: ${fileUploadResponse.statusText}`)
      }

      const uploadResult = (await fileUploadResponse.json()) as FileUploadRes
      const fileId = uploadResult.data.id

      if (!fileId) {
        throw new Error("File id not found in the upload response.")
      }

      // --- Step 2: Create Photo ---
      // 确保数字类型正确
      const createPhotoInput: CreatePhotoInput = {
        ...photoData,
        width: photoData.width ? parseInt(String(photoData.width)) : undefined,
        height: photoData.height
          ? parseInt(String(photoData.height))
          : undefined,
        authorId,
        fileId: fileId,
      }

      const newPhoto = await createPhoto(createPhotoInput)

      if (newPhoto) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Photo uploaded and created successfully!",
        })
        onUploadSuccess()
        resetAndCloseDialog()
      } else {
        throw new Error("Failed to create photo record after upload.")
      }
    } catch (error: any) {
      console.error("Upload process failed:", error)
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "An unexpected error occurred.",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const dialogFooter = (
    <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
      <StButton
        label="Cancel"
        icon="pi pi-times"
        onClick={resetAndCloseDialog}
        className="p-button-text"
      />
      <StButton
        label="Confirm & Upload"
        icon="pi pi-check"
        onClick={handleSubmit}
        loading={isSubmitting}
      />
    </div>
  )

  return (
    <>
      <Toast ref={toast} />
      <StButton
        label="Upload Photo"
        icon="pi pi-upload"
        onClick={() => setDialogVisible(true)}
      />

      <Dialog
        header="Upload New Photo"
        visible={dialogVisible}
        style={{ width: "60vw", minWidth: "400px" }}
        contentStyle={{ padding: "1.5rem" }}
        onHide={resetAndCloseDialog}
        footer={dialogFooter}
        modal
        maskStyle={{ backgroundColor: "#00000021" }}
      >
        <div className="p-fluid grid formgrid">
          {/* 文件上传区域 */}
          <div className="field col-12">
            <label>Photo File *</label>
            <FileUpload
              mode="advanced"
              accept="image/*"
              maxFileSize={10000000} // 10MB
              onSelect={handleFileSelect}
              customUpload
              uploadHandler={() => {}} // 阻止默认上传行为
              chooseLabel="Select Image"
              emptyTemplate={
                <p className="m-0 text-center">Drag and drop file here.</p>
              }
            />
          </div>

          {/* 第一行：标题和 Slug */}
          <div className="field col-12 md:col-6">
            <label htmlFor="title">Title *</label>
            <StInputText
              id="title"
              name="title"
              value={photoData.title}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="slug">Slug *</label>
            <StInputText
              id="slug"
              name="slug"
              value={photoData.slug}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* 第二行：状态和画廊 */}
          <div className="field col-12 md:col-6">
            <label htmlFor="status">Status</label>
            <Dropdown
              id="status"
              value={photoData.status}
              options={statusOptions}
              onChange={(e) => handleValueChange("status", e.value)}
              placeholder="Select Status"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="gallerySlug">Gallery Slug</label>
            <StInputText
              id="gallerySlug"
              name="gallerySlug"
              value={photoData.gallerySlug ?? ""}
              onChange={handleInputChange}
            />
          </div>

          {/* 第三行：尺寸 */}
          <div className="field col-6 md:col-3">
            <label htmlFor="width">Width (px)</label>
            <InputNumber
              id="width"
              value={photoData.width}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                handleValueChange("width", e.value)
              }
              useGrouping={false}
            />
          </div>
          <div className="field col-6 md:col-3">
            <label htmlFor="height">Height (px)</label>
            <InputNumber
              id="height"
              value={photoData.height}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                handleValueChange("height", e.value)
              }
              useGrouping={false}
            />
          </div>
          {/* 拍摄日期 */}
          <div className="field col-12 md:col-6">
            <label htmlFor="dateTaken">Date Taken</label>
            {/* 使用 Calendar，但存储为 string */}
            <Calendar
              id="dateTaken"
              value={photoData.dateTaken ? new Date(photoData.dateTaken) : null}
              onChange={(e) =>
                handleValueChange(
                  "dateTaken",
                  e.value ? (e.value as Date).toISOString() : ""
                )
              }
              showIcon
              showTime
              dateFormat="yy-mm-dd"
            />
          </div>

          {/* Alt Text */}
          <div className="field col-12">
            <label htmlFor="altText">Alt Text (Accessibility)</label>
            <StInputText
              id="altText"
              name="altText"
              value={photoData.altText}
              onChange={handleInputChange}
            />
          </div>

          {/* 描述 */}
          <div className="field col-12">
            <label htmlFor="description">Description</label>
            <InputTextarea
              id="description"
              name="description"
              value={photoData.description ?? ""}
              onChange={handleInputChange}
              rows={3}
              autoResize
            />
          </div>

          {/* 元数据字段 (Location & Exif) */}
          <div className="field col-12 md:col-6">
            <label htmlFor="locationData">Location Data (JSON/String)</label>
            <InputTextarea
              id="locationData"
              name="locationData"
              value={photoData.locationData ?? ""}
              onChange={handleInputChange}
              rows={2}
            />
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="exifData">EXIF Data (JSON/String)</label>
            <InputTextarea
              id="exifData"
              name="exifData"
              value={photoData.exifData ?? ""}
              onChange={handleInputChange}
              rows={2}
            />
          </div>
        </div>
      </Dialog>
    </>
  )
}
